/**
 * How many firms and people use Bidmio, as the ERP publishes it.
 *
 * The ERP owns the numbers: it counts real customers once a day, rounds them
 * down and answers `GET https://app.bidmio.com/public/platform-counters`.
 * It says `show: false` while the owner has not switched the counters on or
 * while the numbers are too small to help; the site then shows nothing new.
 * What lives here is only the shape both sides agree on.
 */

/** Far above any real count; anything larger is treated as garbage. */
export const COUNTER_MAX = 10_000_000;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface PlatformCounters {
  organizations: number;
  users: number;
  /** Day the ERP counted, `YYYY-MM-DD`. */
  asOf: string;
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 && value <= COUNTER_MAX;
}

/**
 * Read an untrusted payload into counters, or `null` when they must not show.
 *
 * Anything but an explicit `show: true` with two sane counts and a date is
 * `null`, so a malformed or hostile answer can never put a number on the page.
 */
export function sanitizeCounters(payload: unknown): PlatformCounters | null {
  const body = payload as { show?: unknown; organizations?: unknown; users?: unknown; as_of?: unknown } | null;
  if (!body || body.show !== true) return null;
  const { organizations, users, as_of: asOf } = body;
  if (!isCount(organizations) || !isCount(users)) return null;
  if (typeof asOf !== "string" || !DATE_RE.test(asOf)) return null;
  return { organizations, users, asOf };
}

const NUMBER_LOCALES: Record<string, string> = {
  sk: "sk-SK",
  cs: "cs-CZ",
  da: "da-DK",
  de: "de-DE",
  en: "en-GB",
};

/** Write `value` the way readers of `lang` expect: `1 200`, `1.200` or `1,200`. */
export function formatCount(value: number, lang: string): string {
  return new Intl.NumberFormat(NUMBER_LOCALES[lang] ?? "en-GB", { maximumFractionDigits: 0 }).format(value);
}
